import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Badge } from './ui/badge'
import { Label } from './ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Sparkles, Loader2, ChevronRight, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import RichTextEditor from './RichTextEditor'

/**
 * QuoteTermsManager
 * Handles: Zahlungsbedingungen, Lieferzeit, Gültigkeit, Gewährleistung,
 *          Projektbeschreibung, KI-Intro Generator, Nächste Schritte, Argumentation
 */

// Branchen + Ziel-Vorschläge für den KI-Intro-Generator.
// (Fehlten komplett — die Komponente crashte beim Rendern von Schritt 3.)
const industries = [
  'Handwerk', 'Hausverwaltung', 'Immobilien', 'Gastronomie', 'Arztpraxis / Gesundheit',
  'Kanzlei / Beratung', 'Industrie / Produktion', 'Einzelhandel / E-Commerce',
  'Verein / Non-Profit', 'Dienstleistung', 'Sonstige'
];

const getProjectGoalsByIndustry = (industry) => {
  const common = [
    'Neue Website erstellen', 'Website-Relaunch', 'Mehr Anfragen über Google (SEO)',
    'Google Ads / schnelle Sichtbarkeit', 'Mitarbeitergewinnung', 'Online-Terminbuchung',
    'Professionelleres Erscheinungsbild', 'Eigene Eingabe'
  ];
  const extra = {
    'Hausverwaltung': ['Verwalterwechsel-Anfragen gewinnen', 'Eigentümer-Portal / Service-Seiten'],
    'Immobilien': ['Objekte besser präsentieren (360°/Fotos)', 'Verkäufer-Leads gewinnen'],
    'Gastronomie': ['Reservierungen steigern', 'Speisekarte & Bewertungen präsentieren'],
    'Handwerk': ['Regionale Auffindbarkeit', 'Referenzen professionell zeigen'],
  };
  return [...(extra[industry] || []), ...common];
};

// Aktueller Status des Kunden — je Branche leicht angepasst (fürs KI-Intro)
const getCurrentStatusByIndustry = (industry) => {
  const common = [
    'Keine Website vorhanden', 'Veraltete Website vorhanden',
    'Website vorhanden, aber kaum Anfragen', 'Kaum sichtbar bei Google',
    'Auftritt wirkt nicht mehr zeitgemäß', 'Eigene Eingabe',
  ];
  const extra = {
    'Hausverwaltung': ['Wachstum geplant — Auftritt hält nicht mit', 'Übernahme/Nachfolge — Neuaufstellung'],
    'Immobilien': ['Objekte werden unter Wert präsentiert'],
    'Gastronomie': ['Gäste finden Speisekarte/Zeiten nicht online'],
    'Handwerk': ['Aufträge kommen nur über Empfehlungen'],
  };
  return [...(extra[industry] || []), ...common];
};

const QuoteTermsManager = ({
  // Payment
  paymentTerms, setPaymentTerms,
  customPaymentTerms, setCustomPaymentTerms,
  savedCustomTerms,
  // Delivery
  deliveryTime, setDeliveryTime,
  validity, setValidity,
  warranty, setWarranty,
  // Content
  additionalInfo, setAdditionalInfo,
  quoteIntro, setQuoteIntro,
  heroHeadline, setHeroHeadline,
  heroSubline, setHeroSubline,
  nextSteps, setNextSteps,
  // AI Intro
  isIntroDialogOpen, setIsIntroDialogOpen,
  isGeneratingIntro,
  introFormData, setIntroFormData,
  tempGeneratedIntro, setTempGeneratedIntro,
  tempAdditionalInfo, setTempAdditionalInfo,
  generateAIIntro,
  handleUseIntro,
  // Collapsible
  isZahlungsbedingungenOpen, setIsZahlungsbedingungenOpen,
  // Optional (nicht jeder Aufrufer liefert sie — Defaults verhindern Crashes)
  customerArguments = '', setCustomerArguments = () => {},
}) => {
  // Eigene Zahlungsbedingung lokal merken (Parent liefert nur savedCustomTerms)
  const saveCustomPaymentTerm = () => {
    if (!customPaymentTerms || !customPaymentTerms.trim()) {
      toast.error('Bitte erst eine eigene Zahlungsbedingung eingeben');
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem('customPaymentTerms') || '[]');
      if (!saved.includes(customPaymentTerms)) {
        saved.push(customPaymentTerms);
        localStorage.setItem('customPaymentTerms', JSON.stringify(saved));
      }
      toast.success('Zahlungsbedingung gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleResetForm = () => {
    setIntroFormData({ industry: '', project_goal: '', website_url: '', contact_person: '', custom_project_goal: '' });
    setTempGeneratedIntro('');
    if (setTempAdditionalInfo) setTempAdditionalInfo('');
  };

  return (
    <div className="space-y-4">
{/* Zahlungsbedingungen */}
<Card>
  <Collapsible open={isZahlungsbedingungenOpen} onOpenChange={setIsZahlungsbedingungenOpen}>
    <CardHeader className="cursor-pointer" onClick={() => setIsZahlungsbedingungenOpen(!isZahlungsbedingungenOpen)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CardTitle className="text-slate-800">💳 Zahlungsbedingungen</CardTitle>
          {paymentTerms && (
            <Badge variant="secondary">
              {paymentTerms}
            </Badge>
          )}
        </div>
        <ChevronRight className={`w-5 h-5 transition-transform ${isZahlungsbedingungenOpen ? 'rotate-90' : ''}`} />
      </div>
    </CardHeader>
    <CollapsibleContent>
      <CardContent>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">
          Wählen Sie die Zahlungsbedingungen für dieses Angebot:
        </label>
        
        <div className="space-y-3">
          {/* Vordefinierte Optionen */}
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="50-50"
              name="payment-terms"
              value="50-50"
              checked={paymentTerms === '50-50'}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="text-slate-500"
            />
            <label htmlFor="50-50" className="text-sm text-slate-700">
              50 % des einmaligen Aufwands bei Beauftragung, 50 % bei Fertigstellung (Standard)
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="50-30-20"
              name="payment-terms"
              value="50-30-20"
              checked={paymentTerms === '50-30-20'}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="text-slate-500"
            />
            <label htmlFor="50-30-20" className="text-sm text-slate-700">
              50 % bei Beauftragung, 30 % bei Zwischenabnahme, 20 % bei Fertigstellung
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="30-tage-netto"
              name="payment-terms"
              value="30-tage-netto"
              checked={paymentTerms === '30-tage-netto'}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="text-slate-500"
            />
            <label htmlFor="30-tage-netto" className="text-sm text-slate-700">
              30 Tage netto
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="14-tage-netto"
              name="payment-terms"
              value="14-tage-netto"
              checked={paymentTerms === '14-tage-netto'}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="text-slate-500"
            />
            <label htmlFor="14-tage-netto" className="text-sm text-slate-700">
              14 Tage netto
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="sofort-faellig"
              name="payment-terms"
              value="sofort-faellig"
              checked={paymentTerms === 'sofort-faellig'}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="text-slate-500"
            />
            <label htmlFor="sofort-faellig" className="text-sm text-slate-700">
              Sofort fällig
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="custom"
              name="payment-terms"
              value="custom"
              checked={paymentTerms === 'custom'}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="text-slate-500"
            />
            <label htmlFor="custom" className="text-sm text-slate-700">
              Benutzerdefiniert:
            </label>
          </div>
          
          {/* Freitext für benutzerdefinierte Bedingungen */}
          {paymentTerms === 'custom' && (
            <div className="ml-6">
              <textarea
                value={customPaymentTerms}
                onChange={(e) => setCustomPaymentTerms(e.target.value)}
                placeholder="z.B. 50% Anzahlung, Rest bei Lieferung..."
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                rows={2}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={saveCustomPaymentTerm}
                className="mt-2"
              >
                Für Zukunft speichern
              </Button>
            </div>
          )}
          
          {/* Gespeicherte benutzerdefinierte Bedingungen */}
          {savedCustomTerms.length > 0 && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <h4 className="text-xs font-medium text-slate-700 mb-2">Gespeicherte Bedingungen:</h4>
              <div className="space-y-2">
                {savedCustomTerms.map((term, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id={`saved-${index}`}
                      name="payment-terms"
                      value={`saved-${index}`}
                      checked={paymentTerms === `saved-${index}`}
                      onChange={(e) => {
                        setPaymentTerms(e.target.value)
                        setCustomPaymentTerms(term)
                      }}
                      className="text-slate-500"
                    />
                    <label htmlFor={`saved-${index}`} className="text-xs text-slate-600">
                      {term}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </CardContent>
</CollapsibleContent>
              </Collapsible>
            </Card>

{/* Lieferzeit, Gültigkeit, Gewährleistung */}
<Card className="mt-6">
  <CardHeader>
    <CardTitle className="text-slate-800">📋 Weitere Vertragsbedingungen</CardTitle>
  </CardHeader>
  <CardContent>
    <div className="space-y-6">
      {/* Lieferzeit */}
      <div>
        <Label htmlFor="delivery-time" className="text-sm font-medium text-slate-700 mb-2 block">
          🚚 Lieferzeit
        </Label>
        <Input
          id="delivery-time"
          value={deliveryTime}
          onChange={(e) => setDeliveryTime(e.target.value)}
          placeholder="z.B. 4-6 Wochen nach Auftragserteilung"
          className="w-full"
        />
      </div>

      {/* Gültigkeit */}
      <div>
        <Label htmlFor="validity" className="text-sm font-medium text-slate-700 mb-2 block">
          📅 Angebotsgültigkeit
        </Label>
        <Input
          id="validity"
          value={validity}
          onChange={(e) => setValidity(e.target.value)}
          placeholder="z.B. 8 Tage gültig ab Erstellungsdatum"
          className="w-full"
        />
      </div>

      {/* Gewährleistung */}
      <div>
        <Label htmlFor="warranty" className="text-sm font-medium text-slate-700 mb-2 block">
          🛡️ Gewährleistung
        </Label>
        <Input
          id="warranty"
          value={warranty}
          onChange={(e) => setWarranty(e.target.value)}
          placeholder="z.B. 12 Monate Gewährleistung"
          className="w-full"
        />
      </div>
    </div>
  </CardContent>
</Card>

{/* Projektbeschreibung & KI Intro */}
<Card className="mt-6">
  <CardHeader>
    <CardTitle className="text-slate-800">📝 Projektbeschreibung & Intro</CardTitle>
  </CardHeader>
  <CardContent>
    <div className="space-y-6">
      {/* Projektbeschreibung */}
      <div>
        <Label htmlFor="additional-info" className="text-sm font-medium text-slate-700 mb-2 block">
          📋 Projektbeschreibung / Zusätzliche Informationen
        </Label>
        <RichTextEditor
          value={additionalInfo}
          onChange={setAdditionalInfo}
          placeholder="Beschreiben Sie das Projekt, besondere Anforderungen oder zusätzliche Informationen..."
          height={150}
        />
        <p className="text-xs text-slate-500 mt-2">
          Diese Informationen werden im Angebot unter &quot;Projektbeschreibung&quot; angezeigt.
        </p>
      </div>

      {/* KI Intro Generierung - mit Dialog */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-slate-700">✨ KI-generiertes Angebots-Intro</h4>
          <Dialog open={isIntroDialogOpen} onOpenChange={setIsIntroDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                KI Intro generieren
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  KI Angebots-Intro Generator
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Firmenname *</Label>
                    <Input
                      id="company_name"
                      value={introFormData.company_name}
                      onChange={(e) => setIntroFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Name des Kunden-Unternehmens"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact_person">Ansprechpartner</Label>
                    <Input
                      id="contact_person"
                      value={introFormData.contact_person}
                      onChange={(e) => setIntroFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                      placeholder="Name der Kontaktperson"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="industry">Branche *</Label>
                    <Select 
                      value={introFormData.industry} 
                      onValueChange={(value) => setIntroFormData(prev => ({ ...prev, industry: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Branche auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map((industry) => (
                          <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="project_goal">Projektziel</Label>
                    <Select 
                      value={introFormData.project_goal} 
                      onValueChange={(value) => setIntroFormData(prev => ({ ...prev, project_goal: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Ziel auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {getProjectGoalsByIndustry(introFormData.industry).map((goal) => (
                          <SelectItem key={goal} value={goal}>{goal}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="website_url">Aktuelle Website (falls vorhanden)</Label>
                    <Input
                      id="website_url"
                      value={introFormData.website_url}
                      onChange={(e) => setIntroFormData(prev => ({ ...prev, website_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                  
                  {/* Custom Project Goal Field */}
                  {introFormData.project_goal === 'Eigene Eingabe' && (
                    <div>
                      <Label htmlFor="custom_project_goal">Eigenes Projektziel</Label>
                      <Input
                        id="custom_project_goal"
                        value={introFormData.custom_project_goal || ''}
                        onChange={(e) => setIntroFormData(prev => ({ ...prev, custom_project_goal: e.target.value }))}
                        placeholder="Beschreiben Sie Ihr individuelles Projektziel..."
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="current_status">Aktueller Status *</Label>
                    <Select 
                      value={introFormData.current_status} 
                      onValueChange={(value) => setIntroFormData(prev => ({ ...prev, current_status: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Status auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {getCurrentStatusByIndustry(introFormData.industry).map((status) => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Custom Current Status Field */}
                  {introFormData.current_status === 'Eigene Eingabe' && (
                    <div>
                      <Label htmlFor="custom_current_status">Eigener aktueller Status</Label>
                      <Input
                        id="custom_current_status"
                        value={introFormData.custom_current_status || ''}
                        onChange={(e) => setIntroFormData(prev => ({ ...prev, custom_current_status: e.target.value }))}
                        placeholder="Beschreiben Sie Ihren aktuellen Status..."
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="timeline">Zeitrahmen</Label>
                    <Select 
                      value={introFormData.timeline || '2-3 Monate'} 
                      onValueChange={(value) => setIntroFormData(prev => ({ ...prev, timeline: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Zeitrahmen wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2 Monate">1-2 Monate</SelectItem>
                        <SelectItem value="2-3 Monate">2-3 Monate</SelectItem>
                        <SelectItem value="3-4 Monate">3-4 Monate</SelectItem>
                        <SelectItem value="4-5 Monate">4-5 Monate</SelectItem>
                        <SelectItem value="5-6 Monate">5-6 Monate</SelectItem>
                        <SelectItem value="6-12 Monate">6-12 Monate</SelectItem>
                        <SelectItem value="Flexibel">Flexibel</SelectItem>
                        <SelectItem value="Eigene Eingabe">Eigene Eingabe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Custom Timeline Field */}
                  {introFormData.timeline === 'Eigene Eingabe' && (
                    <div>
                      <Label htmlFor="custom_timeline">Eigener Zeitrahmen</Label>
                      <Input
                        id="custom_timeline"
                        value={introFormData.custom_timeline || ''}
                        onChange={(e) => setIntroFormData(prev => ({ ...prev, custom_timeline: e.target.value }))}
                        placeholder="z.B. 8-10 Wochen..."
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="challenges">Herausforderungen/Probleme</Label>
                  <Textarea
                    id="challenges"
                    value={introFormData.challenges}
                    onChange={(e) => setIntroFormData(prev => ({ ...prev, challenges: e.target.value }))}
                    placeholder="Welche Probleme soll die neue Website lösen?"
                  />
                </div>
                
                <div>
                  <Label htmlFor="special_requirements">Besondere Anforderungen</Label>
                  <Textarea
                    id="special_requirements"
                    value={introFormData.special_requirements}
                    onChange={(e) => setIntroFormData(prev => ({ ...prev, special_requirements: e.target.value }))}
                    placeholder="Spezielle Wünsche oder Anforderungen"
                  />
                </div>
                
                <div>
                  <Label htmlFor="tone">Stil des Angebots</Label>
                  <Select 
                    value={introFormData.tone} 
                    onValueChange={(value) => setIntroFormData(prev => ({ ...prev, tone: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Stil auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professionell & Sachlich</SelectItem>
                      <SelectItem value="friendly">Freundlich & Persönlich</SelectItem>
                      <SelectItem value="technical">Technisch & Detailliert</SelectItem>
                      <SelectItem value="creative">Kreativ & Innovativ</SelectItem>
                      <SelectItem value="formal">Formal & Konservativ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={generateAIIntro} 
                    disabled={isGeneratingIntro || !introFormData.company_name || !introFormData.industry || !introFormData.current_status}
                    className="flex-1"
                  >
                    {isGeneratingIntro ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Generiere...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Intro generieren
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleResetForm}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Zurücksetzen
                  </Button>
                </div>
                
                {tempGeneratedIntro && (
                  <div className="space-y-3 border-t pt-4">
                    <div>
                      <Label>Generierter Intro:</Label>
                      <Textarea 
                        value={tempGeneratedIntro}
                        onChange={(e) => setTempGeneratedIntro(e.target.value)}
                        className="min-h-24"
                      />
                    </div>
                    {tempAdditionalInfo && (
                      <div>
                        <Label>Zusätzliche Informationen:</Label>
                        <Textarea 
                          value={tempAdditionalInfo}
                          onChange={(e) => setTempAdditionalInfo(e.target.value)}
                          className="min-h-20"
                        />
                      </div>
                    )}
                    <Button onClick={handleUseIntro} className="w-full">
                      Intro verwenden
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Hero-Texte (steuern die großen Layouts: Pitch / Apple / Linear / Swiss / Premium) */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Hero-Headline (optional)
          </Label>
          <input
            type="text"
            value={heroHeadline || ''}
            onChange={(e) => setHeroHeadline && setHeroHeadline(e.target.value)}
            placeholder='z.B. "Mehr Sichtbarkeit für Ihre Immobilien."'
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <Label className="text-sm font-medium text-slate-700 mb-2 mt-3 block">
            Hero-Subline (optional)
          </Label>
          <input
            type="text"
            value={heroSubline || ''}
            onChange={(e) => setHeroSubline && setHeroSubline(e.target.value)}
            placeholder='z.B. "In 8 Wochen online."'
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <p className="text-xs text-slate-500 mt-2">
            Die große Schlagzeile auf der Angebotsseite — wird in den Layouts Pitch, Apple, Linear, Swiss & Premium prominent angezeigt.
          </p>
        </div>

        {/* Intro Text Display/Edit */}
        <div>
          <Label htmlFor="quote-intro" className="text-sm font-medium text-slate-700 mb-2 block">
            Angebots-Intro (optional - kann auch manuell geschrieben werden)
          </Label>
          <RichTextEditor
            value={quoteIntro}
            onChange={setQuoteIntro}
            placeholder="Ein personalisiertes Intro für Ihr Angebot... (kann mit KI generiert werden)"
            height={150}
          />
          <p className="text-xs text-slate-500 mt-2">
            Dieses Intro erscheint am Anfang des Angebots und gibt dem Kunden einen persönlichen Eindruck.
          </p>
        </div>
      </div>

      {/* Was kommt als Nächstes */}
      <div className="border-t pt-6">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">
          🚀 Was kommt als Nächstes? (optional)
        </Label>
        <RichTextEditor
          value={nextSteps}
          onChange={setNextSteps}
          placeholder="z.B. 1. Kickoff-Meeting vereinbaren 2. Konzept erstellen 3. Umsetzung starten..."
          height={120}
        />
        <p className="text-xs text-slate-500 mt-2">
          Wird auf der Angebotsseite nach der Annahme angezeigt.
        </p>
      </div>

      {/* Argumentation für Kunden */}
      <div className="border-t pt-6">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">
          💡 Warum dieses Angebot für Sie? (optional)
        </Label>
        <RichTextEditor
          value={customerArguments}
          onChange={setCustomerArguments}
          placeholder="z.B. Basierend auf Ihren Anforderungen haben wir genau die richtigen Leistungen zusammengestellt..."
          height={120}
        />
        <p className="text-xs text-slate-500 mt-2">
          Persönliche Argumentation — erscheint auf der Angebotsseite für den Kunden. Nutzen Sie Sales-Coaching-Tipps als Grundlage.
        </p>
      </div>
    </div>
  </CardContent>
</Card>


    </div>
  )
}

export default QuoteTermsManager
